/**
 * Architect tab inside CardDetailSlideOver — surfaces the parent
 * decomposition run + sibling cards spawned by the same run.
 *
 * Visible only when the card has an `architectRunId` linkage (see the
 * gateway `/api/architect/cards/:cardId/run` route). Shows:
 *   - Run title + status badge
 *   - Compact list of nodes with state
 *   - Siblings — links back to the same board's cards
 *   - "Open run" deep-link
 */
import type { ArchitectRun } from '@chaos/types/architect';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import type * as React from 'react';

import { architectApi, architectKeys } from '../../lib/api/architect';
import { cn } from '../../lib/utils';

interface ArchitectTabProps {
  cardId: string;
  boardId: string;
}

function nodeStateClass(state: ArchitectRun['nodes'][number]['state']): string {
  switch (state) {
    case 'done':
      return 'bg-success/15 text-success';
    case 'running':
      return 'bg-foreground/8 text-foreground';
    case 'failed':
      return 'bg-error/15 text-error';
    case 'skipped':
      return 'bg-warning/15 text-warning';
    default:
      return 'bg-muted text-muted-foreground';
  }
}

function runStatusClass(status: ArchitectRun['status']): string {
  switch (status) {
    case 'succeeded':
      return 'bg-success/15 text-success';
    case 'running':
      return 'bg-foreground/8 text-foreground';
    case 'failed':
      return 'bg-error/15 text-error';
    case 'cancelled':
      return 'bg-warning/15 text-warning';
    default:
      return 'bg-muted text-muted-foreground';
  }
}

function formatLabel(name: string): string {
  return name
    .split('_')
    .map((p, i) => (i === 0 ? p.charAt(0).toUpperCase() + p.slice(1) : p))
    .join(' ');
}

export function ArchitectTab({ cardId, boardId }: ArchitectTabProps): React.JSX.Element {
  const q = useQuery({
    queryKey: architectKeys.cardRun(cardId),
    queryFn: () => architectApi.getCardRun(cardId),
  });

  if (q.isLoading) {
    return <p className="text-[12px] text-muted-foreground">Loading…</p>;
  }
  if (q.error || !q.data || !q.data.run) {
    return (
      <p className="text-[12px] text-muted-foreground/70">
        This card was not created by the Architect.
      </p>
    );
  }

  const run = q.data.run;
  const siblings = q.data.siblings.filter((s) => s.id !== cardId);

  return (
    <section className="flex flex-col gap-5">
      <header className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <span
            className={cn(
              'rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider',
              runStatusClass(run.status),
            )}
          >
            {run.status}
          </span>
          <span className="text-[11px] text-muted-foreground">
            {new Date(run.startedAt).toLocaleString()}
          </span>
        </div>
        <p className="text-[13px] text-foreground">{run.description}</p>
        <Link
          to="/architect/run/$runId"
          params={{ runId: run.id }}
          className="text-[12px] text-muted-foreground transition-colors hover:text-foreground hover:underline"
        >
          Open run →
        </Link>
      </header>

      <div>
        <h4 className="mb-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
          Workflow
        </h4>
        <ul className="flex flex-col gap-1">
          {run.nodes
            .slice()
            .sort((a, b) => a.position - b.position)
            .map((n) => (
              <li key={n.id} className="flex items-center justify-between gap-2 text-[12px]">
                <span className="text-foreground">{formatLabel(n.name)}</span>
                <span
                  className={cn(
                    'rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider',
                    nodeStateClass(n.state),
                  )}
                >
                  {n.state}
                </span>
              </li>
            ))}
        </ul>
      </div>

      <div>
        <h4 className="mb-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
          Siblings ({siblings.length})
        </h4>
        {siblings.length === 0 ? (
          <p className="text-[12px] text-muted-foreground/70">
            This card has no siblings from the same decomposition.
          </p>
        ) : (
          <ul className="flex flex-col gap-1">
            {siblings.map((s) => (
              <li key={s.id}>
                <Link
                  to="/board/$boardId/card/$cardId"
                  params={{ boardId: s.boardId ?? boardId, cardId: s.id }}
                  className="block truncate rounded px-2 py-1 text-[12px] text-foreground transition-colors hover:bg-foreground/5"
                >
                  {s.title}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
